<script setup>
  /* eslint-disable vue/no-v-html */
  import {
    ref,
    shallowRef as shallow_ref,
    onMounted as mounted,
    onBeforeUnmount as before_unmount,
    watch
  } from 'vue'
  import { report_symbol_ready } from '@/use/symbol-ready'
  import { load_from_cache } from '@/utils/itemid'
  import { hydrate } from '@/utils/item'
  import { get } from 'idb-keyval'

  const props = defineProps({
    itemid: {
      type: String,
      required: true
    }
  })

  const element = ref(null)
  const symbol_content = shallow_ref('')
  const symbol_id = ref('')
  const symbol_viewbox = ref('')
  const load_token = ref(0)

  /**
   * The id this instance counted in the shared ready registry, if it got that
   * far. A copy that unmounts before its load lands - open a poster in a
   * dialog, close it again - must not decrement a count it never added, or
   * the copy that stayed loses its layers and renders blank.
   */
  const counted = ref('')

  const release = () => {
    if (!counted.value) return
    report_symbol_ready(counted.value, false)
    counted.value = ''
  }

  const load_symbol = async () => {
    const token = ++load_token.value
    const itemid_at_start = props.itemid
    let html_string = await get(itemid_at_start)
    if (!html_string) {
      const { html } = await load_from_cache(itemid_at_start)
      if (!html) return
      html_string = html
    }
    if (load_token.value !== token) return

    const fragment = hydrate(html_string)
    if (!fragment) return
    const loaded = fragment.querySelector(`[itemid="${itemid_at_start}"]`)
    if (loaded) {
      symbol_content.value = loaded.innerHTML
      symbol_id.value = loaded.id || itemid_at_start
      symbol_viewbox.value = loaded.getAttribute('viewBox') || ''
      report_symbol_ready(itemid_at_start, true)
      counted.value = itemid_at_start
    }
  }

  mounted(load_symbol)
  watch(
    () => props.itemid,
    () => {
      release()
      symbol_content.value = ''
      load_symbol()
    }
  )

  /**
   * Unmount is not the place for this: a load still in flight resolves after
   * the instance is gone, so the token is bumped first to keep that late reply
   * from reporting ready, then any count it did add is released.
   */
  before_unmount(() => {
    load_token.value++
    release()
  })
</script>

<template>
  <symbol
    ref="element"
    :id="symbol_id"
    v-bind:viewBox="symbol_viewbox || undefined"
    :itemid="itemid"
    v-html="symbol_content" />
</template>
